import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PasswordInput } from "@/shared/ui/password-input";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { useAuth } from "@/features/auth/model/AuthContext";
import { useLogin, useOAuthRedirect } from "@/features/auth/hooks/auth.hooks";
import { VersionInfo } from "@/shared/widgets/VersionInfo";
import { AuthLayout } from "./AuthLayout";
import { AuthCard, AuthCardHeader, AuthCardContent } from "./AuthCard";
import { LogoHeader, AuthHeader } from "./AuthHeader";
import { useTranslation } from "@/shared/i18n";

export default function Auth() {
  const { t } = useTranslation("auth");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [isLogin] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login: setAuthUser } = useAuth();

  const oauthMutation = useOAuthRedirect();

  const loginMutation = useLogin({
    onSuccess: (data) => {
      if (data && data.userId) {
        setAuthUser(data);
        if (data.role === "ADMIN") {
          navigate("/");
        } else {
          navigate("/stats");
        }
      }
    },
    onError: (error) => {
      setAuthError(error.message || t("authError"));
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpa o erro do campo ao começar a digitar
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.email) {
      newErrors.email = t("emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("invalidEmail");
    }

    if (!formData.password) {
      newErrors.password = t("passwordRequired");
    } else if (formData.password.length < 6) {
      newErrors.password = t("passwordMinLength");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setAuthError(null);

    try {
      await loginMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
      });
    } catch (error) {
      // Erro já tratado no onError do mutation
      console.error("Login error:", error);
    }
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthCardHeader>
          <LogoHeader />
          <AuthHeader
            title={isLogin ? t("welcomeBack") : t("createAccount")}
            description={isLogin ? t("enterWithEmail") : t("fillDataToCreate")}
          />
        </AuthCardHeader>
        <AuthCardContent>
          {authError && (
            <Alert variant="destructive" className="mb-4 bg-red-900/20 border-red-900 text-white">
              <AlertDescription>{authError}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">
                {t("email")}
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t("emailPlaceholder")}
                  className={`bg-[#2A2A2A] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#D91657] focus-visible:ring-offset-0 ${
                    errors.email ? "border-red-500" : ""
                  }`}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-white/80">
                  {t("password")}
                </Label>
              </div>
              <div className="relative">
                <PasswordInput
                  id="password"
                  name="password"
                  placeholder="••••••••"
                  className={`bg-[#2A2A2A] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#D91657] focus-visible:ring-offset-0 ${
                    errors.password ? "border-red-500" : ""
                  }`}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                />
                {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  className="p-0 self-end h-auto text-xs text-white/60 hover:text-[#D91657] hover:no-underline"
                  onClick={() => navigate("/auth/forgot-password")}
                >
                  {t("forgotPassword")}
                </Button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? t("loading") : isLogin ? t("signIn") : t("createAccount")}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase text-white/40">{t("orContinueWith")}</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 border-white/10 bg-[#2A2A2A] text-white hover:bg-[#333333] hover:text-white"
              disabled={oauthMutation.isPending}
              onClick={() => oauthMutation.mutate("google")}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M23.49 12.27c0-.82-.07-1.6-.21-2.36H12v4.47h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.56-5.16 3.56-8.73z"
                />
                <path
                  fill="#34A853"
                  d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.87-3c-1.07.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.29v3.09A11.99 11.99 0 0 0 12 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.29 14.29A7.18 7.18 0 0 1 4.91 12c0-.8.14-1.57.38-2.29V6.62H1.29a11.99 11.99 0 0 0 0 10.76l4-3.09z"
                />
                <path
                  fill="#EA4335"
                  d="M12 4.77c1.76 0 3.34.61 4.59 1.8l3.43-3.43C17.95 1.19 15.24 0 12 0A11.99 11.99 0 0 0 1.29 6.62l4 3.09C6.23 6.88 8.88 4.77 12 4.77z"
                />
              </svg>
              {t("continueWithGoogle")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 border-white/10 bg-[#2A2A2A] text-white hover:bg-[#333333] hover:text-white"
              disabled={oauthMutation.isPending}
              onClick={() => oauthMutation.mutate("apple")}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.36 12.83c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.39-2.44-1.58-2.97-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.03-1.43 2.48-.37 6.16 1.03 8.17.68.99 1.49 2.09 2.56 2.05 1.03-.04 1.42-.66 2.66-.66s1.59.66 2.68.64c1.11-.02 1.81-1 2.49-1.99.78-1.14 1.1-2.25 1.12-2.31-.02-.01-2.15-.83-2.18-3.29zM14.31 6.4c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.4 1.23-.53.61-.99 1.59-.86 2.52.91.07 1.84-.46 2.41-1.15z" />
              </svg>
              {t("continueWithApple")}
            </Button>
          </div>
        </AuthCardContent>
      </AuthCard>
      <div className="fixed bottom-4 right-4">
        <VersionInfo />
      </div>
    </AuthLayout>
  );
}
